import React, { useEffect, useState, useCallback } from "react";
import { Tag, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { SEASONS } from "@/lib/seasons";

export const ClearanceCard = ({ autoSlug }) => {
  const [slug, setSlug] = useState(autoSlug || "halloween");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback((s) => api.get(`/admin/seasons/${s}/clearance`).then(({ data }) => setData(data)).catch(() => setData(null)), []);
  useEffect(() => { load(slug); }, [slug, load]);

  const switchOn = useCallback(async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/admin/seasons/${slug}/clearance`);
      toast.success(`${r.promo.code} is live — 20% off ${r.items} clearance item${r.items === 1 ? "" : "s"} for 60 days 🏷️`);
      load(slug);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  }, [slug, load]);

  useEffect(() => { if (autoSlug && data && data.slug === autoSlug && !data.promo && data.unsold.length && !busy) switchOn(); }, [autoSlug, data]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async () => {
    try { await api.patch(`/admin/seasons/${slug}/clearance/toggle`); load(slug); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const copy = () => { navigator.clipboard.writeText(data.promo.code); toast.success("Code copied"); };
  const p = data?.promo;
  const live = p && p.active && (!p.expires_at || p.expires_at > new Date().toISOString());

  return (
    <div className="bg-white rounded-3xl soft-shadow p-6 mb-4" data-testid="clearance-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <div className="flex items-center gap-2"><Tag className="w-5 h-5 text-[#FF6B9D]" /><h3 className="font-display font-bold text-[#19324A]">Clearance code — didn't-sell items</h3></div>
        <select value={slug} onChange={(e) => setSlug(e.target.value)} className="rounded-xl border-2 border-[#19324A]/10 px-3 py-1.5 text-sm outline-none focus:border-[#FFD93D]" data-testid="clearance-season">
          {SEASONS.map((s) => <option key={s.slug} value={s.slug}>{s.emoji} {s.label}</option>)}
        </select>
      </div>
      <p className="text-xs text-[#19324A]/60 mb-4">One tap creates a 20% code that only works on the items that didn't sell during the last {data?.label || ""} window{data ? ` (${data.window.start} → ${data.window.end})` : ""}. Valid 60 days; switch it off any time.</p>
      {!data ? null : data.unsold.length === 0 ? (
        <p className="text-sm font-semibold text-[#2E7D32]" data-testid="clearance-all-sold">Everything in {data.label} sold at least once — nothing to clear 🎉</p>
      ) : (
        <>
          <ul className="flex flex-wrap gap-2 mb-4" data-testid="clearance-items">
            {data.unsold.map((u) => (
              <li key={u.slug} className="flex items-center gap-2 bg-[#FFF9EE] rounded-full pl-1 pr-3 py-1 text-xs font-semibold text-[#19324A]" data-testid={`clearance-item-${u.slug}`}>
                {u.image && <img src={u.image} alt="" className="w-6 h-6 rounded-full object-cover" />}{u.name}<span className="text-[#19324A]/50">${u.price.toFixed(2)}</span>
              </li>
            ))}
          </ul>
          {p ? (
            <div className="flex items-center gap-3 flex-wrap" data-testid="clearance-promo">
              <span className={`font-mono font-bold text-sm rounded-xl px-3 py-2 ${live ? "bg-[#7ED957]/20 text-[#19324A]" : "bg-[#19324A]/5 text-[#19324A]/50 line-through"}`} data-testid="clearance-code">{p.code}</span>
              <button onClick={copy} className="btn-outline !px-3 !py-2 text-xs" data-testid="clearance-copy"><Copy className="w-4 h-4" /> Copy</button>
              <button onClick={toggle} className={`!px-4 !py-2 text-xs rounded-full font-bold ${p.active ? "bg-[#19324A]/10 text-[#19324A]" : "bg-[#7ED957] text-[#19324A]"}`} data-testid="clearance-toggle">{p.active ? "Switch off" : "Switch on"}</button>
              <button onClick={switchOn} disabled={busy} className="text-xs text-[#19324A]/60 underline" data-testid="clearance-refresh">{busy ? "Updating…" : "Refresh items & extend 60 days"}</button>
              <span className="text-xs text-[#19324A]/50">{p.used_count || 0} use{p.used_count === 1 ? "" : "s"} · {p.product_slugs?.length || 0} items{p.expires_at ? ` · until ${new Date(p.expires_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}` : ""}</span>
            </div>
          ) : (
            <button onClick={switchOn} disabled={busy} className="btn-pink !px-5 !py-2.5 text-sm" data-testid="clearance-switch-on">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />} Switch on 20% clearance code
            </button>
          )}
        </>
      )}
    </div>
  );
};
